import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCommentDots, 
  faTimes, 
  faStar,
  faPaperPlane,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { getApiUrl, SERVERS, ENDPOINTS } from '../config/api';
import './GlobalFeedback.css';

const GlobalFeedback = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    feedback_text: '',
    feedback_category: 'general',
    user_email: '',
    user_name: '',
    rating: 0,
    page_context: window.location.pathname
  });
  const [hoveredStar, setHoveredStar] = useState(0);

  const categories = [
    { value: 'general', label: 'General Feedback' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'ui_ux', label: 'UI/UX Issue' }
  ];

  const handleOpen = () => {
    setIsOpen(true);
    setIsSuccess(false);
    setFormData(prev => ({
      ...prev,
      page_context: window.location.pathname
    }));
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSuccess(false);
    // Reset form after a delay
    setTimeout(() => {
      setFormData({
        feedback_text: '',
        feedback_category: 'general',
        user_email: '',
        user_name: '',
        rating: 0,
        page_context: window.location.pathname
      });
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.feedback_text.trim()) {
      alert('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        getApiUrl(SERVERS.DATABASE, ENDPOINTS.FEEDBACK_SUBMIT),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            feedback_text: formData.feedback_text,
            feedback_category: formData.feedback_category,
            user_email: formData.user_email || null,
            user_name: formData.user_name || null,
            rating: formData.rating || null,
            page_context: formData.page_context
          }),
        }
      );

      if (response.ok) {
        setIsSuccess(true);
        // Close modal after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        const error = await response.json();
        alert(`Failed to submit feedback: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStarClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  return (
    <>
      {/* Floating Feedback Button */}
      <button 
        className="global-feedback-button"
        onClick={handleOpen}
        title="Share your feedback"
      >
        <FontAwesomeIcon icon={faCommentDots} />
      </button>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="global-feedback-overlay" onClick={handleClose}>
          <div 
            className="global-feedback-modal" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="feedback-modal-header">
              <h3>
                <FontAwesomeIcon icon={faCommentDots} style={{ marginRight: '10px' }} />
                Share Your Feedback
              </h3>
              <button 
                className="feedback-close-btn"
                onClick={handleClose}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {isSuccess ? (
              <div className="feedback-success">
                <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
                <h4>Thank You!</h4>
                <p>Your feedback has been submitted successfully.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="feedback-form">
                <div className="form-group">
                  <label>Feedback Category</label>
                  <select
                    value={formData.feedback_category}
                    onChange={(e) => setFormData(prev => ({ ...prev, feedback_category: e.target.value }))}
                    className="form-control"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Your Feedback *</label>
                  <textarea
                    value={formData.feedback_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, feedback_text: e.target.value }))}
                    placeholder="Tell us what you think, report a bug, or suggest a feature..."
                    rows={5}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Rate Your Experience (Optional)</label>
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FontAwesomeIcon
                        key={star}
                        icon={faStar}
                        className={`star ${star <= (hoveredStar || formData.rating) ? 'active' : ''}`}
                        onClick={() => handleStarClick(star)}
                        onMouseEnter={() => setHoveredStar(star)}
                        onMouseLeave={() => setHoveredStar(0)}
                      />
                    ))}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Your Name (Optional)</label>
                    <input
                      type="text"
                      value={formData.user_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, user_name: e.target.value }))}
                      placeholder="John Doe"
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>Your Email (Optional)</label>
                    <input
                      type="email"
                      value={formData.user_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, user_email: e.target.value }))}
                      placeholder="john@example.com"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '8px' }} />
                        Submit Feedback
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalFeedback;
